import { backgroundScheduler } from '@libs/utils/background_scheduler'

export interface DynamicImageFrame {
  frame: number[]
  durationMs: number
}

interface DynamicImagePlaybackOptions {
  frameIntervalMs: number
  resumeDelayMs: number
  maxConsecutiveFailures: number
  getLoop: () => boolean
  sendFrame: (frame: number[]) => Promise<boolean>
  onFrame: (index: number) => void
  onFinished: () => void
  onWriteFailure: () => void
}

type CancellableTimer = { cancel: () => void }

/** Owns playback timing only; image decoding and device state stay outside. */
export class DynamicImagePlayback {
  private frames: DynamicImageFrame[] = []
  private playTimer: CancellableTimer | null = null
  private resumeTimer: CancellableTimer | null = null
  private token = 0
  private pauseDepth = 0
  private pausedFrameIndex = 0
  private currentFrameIndex = 0
  private lastSentAt = 0
  private ignoreNextFailure = false
  private consecutiveFailures = 0

  constructor(private readonly options: DynamicImagePlaybackOptions) {}

  get hasFrames() {
    return this.frames.length > 0
  }

  setFrames(frames: DynamicImageFrame[]) {
    this.frames = frames
  }

  clearFrames() {
    this.frames = []
  }

  start(index = 0) {
    if (!this.frames.length) return
    this.cancelTimers()
    this.pauseDepth = 0
    this.lastSentAt = 0
    this.consecutiveFailures = 0
    this.runFrom(index, ++this.token)
  }

  stop() {
    this.token++
    this.pauseDepth = 0
    this.ignoreNextFailure = false
    this.consecutiveFailures = 0
    this.lastSentAt = 0
    this.cancelTimers()
  }

  pause() {
    if (!this.frames.length) return false
    this.pauseDepth++
    if (this.pauseDepth > 1) return true
    this.pausedFrameIndex = this.currentFrameIndex
    this.token++
    this.ignoreNextFailure = false
    this.consecutiveFailures = 0
    this.lastSentAt = 0
    this.cancelTimers()
    return true
  }

  resume() {
    if (this.pauseDepth === 0) return
    this.pauseDepth--
    if (this.pauseDepth > 0 || !this.frames.length) return
    this.lastSentAt = 0
    this.consecutiveFailures = 0
    this.ignoreNextFailure = true
    const token = ++this.token
    this.resumeTimer = backgroundScheduler.setTimer(() => {
      this.resumeTimer = null
      if (token === this.token && this.pauseDepth === 0 && this.frames.length) this.runFrom(this.pausedFrameIndex % this.frames.length, token)
    }, this.options.resumeDelayMs)
  }

  private runFrom(index: number, token: number) {
    let frameIndex = index
    this.playTimer = backgroundScheduler.setLoop(async () => {
      const delay = await this.playFrame(frameIndex, token)
      if (delay === null) return null
      frameIndex = (frameIndex + 1) % this.frames.length
      return delay
    }, 0)
  }

  private async playFrame(index: number, token: number) {
    if (token !== this.token || !this.frames.length || this.pauseDepth > 0) return null
    this.currentFrameIndex = index
    this.pausedFrameIndex = index
    this.options.onFrame(index)
    const current = this.frames[index]
    const now = performance.now()
    if (this.lastSentAt === 0 || now - this.lastSentAt >= this.options.frameIntervalMs) {
      this.lastSentAt = now
      const ok = await this.options.sendFrame(current.frame)
      if (token !== this.token || this.pauseDepth > 0) return null
      if (!ok) {
        if (this.ignoreNextFailure) {
          this.ignoreNextFailure = false
          return current.durationMs
        }
        if (++this.consecutiveFailures >= this.options.maxConsecutiveFailures) {
          this.options.onWriteFailure()
          return null
        }
        return current.durationMs
      }
      this.ignoreNextFailure = false
      this.consecutiveFailures = 0
    }

    if (index + 1 >= this.frames.length && !this.options.getLoop()) {
      this.options.onFinished()
      return null
    }
    return current.durationMs
  }

  private cancelTimers() {
    this.playTimer?.cancel()
    this.playTimer = null
    this.resumeTimer?.cancel()
    this.resumeTimer = null
  }
}
